/**
 * ServiceAccountDetailsModal Component
 *
 * Modal for viewing service account details in a read-only format.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useAuth } from '../../../contexts/AuthContext';
import { useGroups } from '../../groups/hooks/useGroups';
import type { ServiceAccountSanitized, ServiceAccountStatus } from '../../../types/service-account.types';
import { RoleType } from '../../../types/role.types';
import { formatDate } from '../../../lib/utils/format';

interface ServiceAccountDetailsModalProps {
  serviceAccount: ServiceAccountSanitized | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Get status badge styling
 */
function getStatusBadge(status: ServiceAccountStatus): { variant: 'success' | 'warning' | 'error' | 'info'; label: string } {
  switch (status) {
    case 'active':
      return { variant: 'success', label: 'Active' };
    case 'inactive':
      return { variant: 'warning', label: 'Inactive' };
    case 'suspended':
      return { variant: 'error', label: 'Suspended' };
    default:
      return { variant: 'info', label: status };
  }
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export const ServiceAccountDetailsModal: React.FC<ServiceAccountDetailsModalProps> = ({
  serviceAccount,
  isOpen,
  onClose,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';

  // Fetch groups to display service account's group memberships
  const { data: allGroups = [] } = useGroups(tenantId);

  if (!serviceAccount) return null;

  const statusBadge = getStatusBadge(serviceAccount.status);
  const appDomainRoles = (serviceAccount.roles || []).filter(r => r.type === RoleType.APP_DOMAIN);
  const otherRoles = (serviceAccount.roles || []).filter(r => r.type !== RoleType.APP_DOMAIN);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>Service Account Details</Modal.Header>

      <Modal.Body>
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium text-sm">
                    {getInitials(serviceAccount.serviceAccountName)}
                  </div>
                  <span className="text-sm text-gray-900">
                    {serviceAccount.serviceAccountName}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Code</dt>
                <dd className="mt-1">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {serviceAccount.serviceAccountCode}
                  </code>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Service Account ID</dt>
                <dd className="mt-1">
                  <CopyableCode value={serviceAccount.serviceAccountId} />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">
                  <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                </dd>
              </div>
              {serviceAccount.description && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="mt-1 text-sm text-gray-900">{serviceAccount.description}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">APP_DOMAIN Roles</dt>
                <dd className="mt-1">
                  {appDomainRoles.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {appDomainRoles.map((role) => (
                        <Badge key={role.roleId} variant="info">
                          {role.roleCode || `${role.roleId.slice(0, 8)}...`}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 italic">No APP_DOMAIN roles assigned</span>
                  )}
                </dd>
              </div>
              {otherRoles.length > 0 && (
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Other Roles</dt>
                  <dd className="mt-1">
                    <div className="flex flex-wrap gap-2">
                      {otherRoles.map((role) => (
                        <Badge key={role.roleId} variant="secondary">
                          {role.roleCode || `${role.roleId.slice(0, 8)}...`}
                        </Badge>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Group Memberships</dt>
                <dd className="mt-1">
                  {serviceAccount.groups && serviceAccount.groups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {serviceAccount.groups.map((groupAssignment) => {
                        const group = allGroups.find((g) => g.groupId === groupAssignment.groupId);
                        return (
                          <Badge key={groupAssignment.groupId} variant="secondary">
                            {group ? group.groupName : groupAssignment.groupCode || `${groupAssignment.groupId.slice(0, 8)}...`}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 italic">Not a member of any groups</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Metadata */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Created At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(serviceAccount.createdAt, 'MMM dd, yyyy HH:mm')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formatDate(serviceAccount.updatedAt, 'MMM dd, yyyy HH:mm')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
